import { z } from 'zod'
import { supabase } from '../lib/supabase'

const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/

export const SchemaSmtp = z.object({
  host: z.string().trim().min(1, 'Server in uscita obbligatorio'),
  porta: z.coerce.number().int().min(1, 'Porta non valida').max(65535, 'Porta non valida'),
  sicurezza: z.enum(['ssl', 'starttls']),
  utente: z.string().trim().min(1, 'Nome utente obbligatorio'),
  // Vuota = lascia quella salvata (0043_smtp_partner.sql): il portale non la
  // rilegge mai, quindi non puo' riproporla.
  password: z.string().transform((v) => (v === '' ? null : v)),
  mittenteNome: z.string().trim().min(1, 'Nome del mittente obbligatorio'),
  mittenteEmail: z.string().trim().regex(EMAIL, 'Email del mittente non valida'),
})

export type DatiSmtp = z.input<typeof SchemaSmtp>

export interface ConfigSmtp {
  readonly host: string
  readonly porta: number
  readonly sicurezza: 'ssl' | 'starttls'
  readonly utente: string
  readonly mittenteNome: string
  readonly mittenteEmail: string
}

export async function caricaSmtp(): Promise<ConfigSmtp | null> {
  const { data, error } = await supabase
    .from('smtp_partner')
    .select('host, porta, sicurezza, utente, mittente_nome, mittente_email')
    .maybeSingle()
  if (error) throw new Error(error.message)
  if (!data) return null
  const r = data as Record<string, unknown>
  return {
    host: r.host as string,
    porta: r.porta as number,
    sicurezza: r.sicurezza as ConfigSmtp['sicurezza'],
    utente: r.utente as string,
    mittenteNome: r.mittente_nome as string,
    mittenteEmail: r.mittente_email as string,
  }
}

/** Passa dalla RPC: la password va cifrata nel Vault, e solo il database puo' farlo. */
export async function salvaSmtp(dati: DatiSmtp): Promise<void> {
  const s = SchemaSmtp.parse(dati)
  const { error } = await supabase.rpc('salva_smtp', {
    p_host: s.host,
    p_porta: s.porta,
    p_sicurezza: s.sicurezza,
    p_utente: s.utente,
    p_password: s.password,
    p_mittente_nome: s.mittenteNome,
    p_mittente_email: s.mittenteEmail,
  })
  if (error) throw new Error(error.message)
}

/** La spedisce il worker (le Edge Function non aprono porte SMTP): qui si
 * accoda e si restituisce l'invio di cui seguire l'esito. */
export async function provaSmtp(destinatario: string): Promise<number> {
  const { data, error } = await supabase.rpc('prova_smtp', { p_destinatario: destinatario })
  if (error) throw new Error(error.message)
  return data as number
}

export async function esitoInvio(id: number): Promise<{ stato: 'in_coda' | 'inviata' | 'errore'; errore: string | null }> {
  const { data, error } = await supabase.from('notifica_inviata').select('stato, errore').eq('id', id).single()
  if (error) throw new Error(error.message)
  return data as { stato: 'in_coda' | 'inviata' | 'errore'; errore: string | null }
}
